import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";


import { CONSULAR_FAQ_CATEGORIES } from "@/content/consular-corpus/faq-defs";
import { CONSULAR_DOC_LANDINGS } from "@/content/consular-corpus/documents";
import { CONSULAR_COUNTRY_NAMES as CONSULAR_COUNTRY_LANDINGS } from "@/content/consular-corpus/names.gen";
import { getActiveProvince } from "@/provinces/_registry";

export const Route = createFileRoute("/consular-legalization/faq/")({
  loader: async (): Promise<{ CONSULAR_FAQ_TH: { q: string; a: string }[]; CONSULAR_KEYWORDS_TH: string[]; CONSULAR_QUESTIONS_TH: string[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/consular-corpus/faq"),
      import("@/content/consular-corpus"),
    ]);
    return { CONSULAR_FAQ_TH: m0.CONSULAR_FAQ_TH, CONSULAR_KEYWORDS_TH: m1.CONSULAR_KEYWORDS_TH, CONSULAR_QUESTIONS_TH: m1.CONSULAR_QUESTIONS_TH };
  },
  component: ConsularFaqHub,
  head: ({ loaderData }) => {
    const { CONSULAR_FAQ_TH = [], CONSULAR_KEYWORDS_TH = [], CONSULAR_QUESTIONS_TH = [] } = loaderData ?? {};

    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `รับรองกงสุล (นิติกรณ์ MFA) FAQ 2026 — ค่าธรรมเนียม ขั้นตอน Apostille | ${config.nameTh}`;
    const description =
      "รวมคำถามที่พบบ่อยเรื่องรับรองเอกสารกงสุล กรมการกงสุล แจ้งวัฒนะ — ค่าธรรมเนียม 200/400 บาท ระยะเวลา การจองคิว หนังสือมอบอำนาจ การรับรองคำแปล และ Apostille 2026 ตอบโดยทีมทนาย";
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/consular-legalization/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/consular-legalization/faq" }],
        keywords: CONSULAR_KEYWORDS_TH.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "รับรองกงสุล FAQ", url: `${site}/consular-legalization/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(CONSULAR_FAQ_TH)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
            keywords: CONSULAR_KEYWORDS_TH.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/consular-legalization/faq")) },
      ],
    };
  },
});

function ConsularFaqHub() {
  const { CONSULAR_FAQ_TH, CONSULAR_KEYWORDS_TH, CONSULAR_QUESTIONS_TH } = Route.useLoaderData() as { CONSULAR_FAQ_TH: { q: string; a: string }[]; CONSULAR_KEYWORDS_TH: string[]; CONSULAR_QUESTIONS_TH: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>รับรองกงสุล FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          รับรองกงสุล / นิติกรณ์ MFA — FAQ {CONSULAR_FAQ_TH.length} ข้อ (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          คำตอบฉบับทนายเรื่องการรับรองเอกสารที่กองสัญชาติและนิติกรณ์ กรมการกงสุล ถนนแจ้งวัฒนะ —
          ค่าธรรมเนียม ระยะเวลา การจองคิว e-Legalization หนังสือมอบอำนาจ การรับรองคำแปล
          และการเปลี่ยนผ่านสู่ Apostille ปี 2026
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/consular-legalization/faq" className="text-primary underline">
            MFA legalization FAQ for expats →
          </a>
        </div>

        <section aria-label="หมวดคำถามรับรองกงสุล" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">หมวดคำถาม</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {CONSULAR_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/consular-legalization/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleTh} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="รับรองนิติกรณ์ตามประเภทเอกสาร" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">รับรองนิติกรณ์ตามประเภทเอกสาร</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_DOC_LANDINGS.map((d) => (
              <a key={d.slug} href={`/consular-legalization/document/${d.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {d.titleTh}
              </a>
            ))}
          </div>
        </section>

        <section aria-label="รับรองเอกสารตามประเทศปลายทาง" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">รับรองเอกสารตามประเทศปลายทาง</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_COUNTRY_LANDINGS.map((c) => (
              <a key={c.slug} href={`/consular-legalization/country/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.nameTh}
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {CONSULAR_FAQ_TH.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ให้เราเดินเรื่องรับรองกงสุลแทน</h2>
          <p className="text-sm text-muted-foreground mb-4">
            รับเอกสารถึงบ้าน → แปล → ยื่นกรมการกงสุล → สถานทูตปลายทาง/Apostille → ส่งคืน EMS/DHL
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ติดต่อทีมงาน →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">คำถามที่ผู้ใช้ค้นหาบ่อย ({CONSULAR_QUESTIONS_TH.length} คำถาม)</h2>
          <ul className="grid gap-1 text-sm text-muted-foreground md:grid-cols-2">
            {CONSULAR_QUESTIONS_TH.slice(0, 120).map((q, i) => (
              <li key={i}>• {q}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">หัวข้อยอดฮิต ({CONSULAR_KEYWORDS_TH.length} คำค้น)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_KEYWORDS_TH.slice(0, 300).map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>
      
        <FaqSourceNote topic="consular" locale="th" />
        <div className="mb-10">
          <PageGallery category="consular" seed="consular-faq.index" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </>
  );
}